import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getApiBaseUrl } from '../../../../core/config/api';
import type { InstanceRow, SectorVehicleRow, VehRow } from './instanceModel';
import { normalizeInterventionInstanceStatut } from '../../../../shared/constants/interventionInstanceStatus';
import {
  effectiveMoyensRequired,
  effectiveTempsSurPlaceMinutes,
  formatDurationMmSs,
  isTerminalInterventionStatus,
} from './instanceModel';
import { useInterventionVehicleActions } from './useInterventionVehicleActions';
import type { CompletionReward } from './useInterventionVehicleActions';

type TranslateFn = (key: string) => string;

export function useInterventionManageController(opts: {
  userId: number | undefined;
  instances: InstanceRow[];
  setInstances: Dispatch<SetStateAction<InstanceRow[]>>;
  loadInstances: () => Promise<void>;
  refreshInstances: () => Promise<void>;
  authHeaders: () => Record<string, string>;
  t: TranslateFn;
  titleLocale: string;
  /** Sidebar : charger les véhicules seulement quand le panneau est ouvert ; synoptique : true */
  fetchVehiclesEnabled: boolean;
}) {
  const {
    userId,
    instances,
    setInstances,
    loadInstances,
    refreshInstances,
    authHeaders,
    t,
    titleLocale,
    fetchVehiclesEnabled,
  } = opts;

  const [manageId, setManageId] = useState<number | null>(null);
  const [vehicles, setVehicles] = useState<VehRow[]>([]);
  const [vehLoading, setVehLoading] = useState(false);
  const [selectedVehIdsByInstance, setSelectedVehIdsByInstance] = useState<Record<number, number[]>>(
    {}
  );
  const [sectorVehicles, setSectorVehicles] = useState<SectorVehicleRow[]>([]);
  const [sectorAlreadyEngaged, setSectorAlreadyEngaged] = useState<
    Array<{
      id: number;
      nom?: string | null;
      type_nom?: string | null;
      statut?: string | null;
      eta_remaining_sec?: number | null;
    }>
  >([]);
  const [sectorLoading, setSectorLoading] = useState(false);
  const [sectorError, setSectorError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [uiTick, setUiTick] = useState(0);
  const [sectorRefreshNonce, setSectorRefreshNonce] = useState(0);
  const [completionRewards, setCompletionRewards] = useState<CompletionReward | null>(null);

  const tRef = useRef(t);
  tRef.current = t;
  const authHeadersRef = useRef(authHeaders);
  authHeadersRef.current = authHeaders;

  const loadVehicles = useCallback(async () => {
    if (!userId) return;
    setVehLoading(true);
    try {
      const base = getApiBaseUrl();
      const res = await fetch(`${base}/api/buildings/vehicules`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success && Array.isArray(data.data)) {
        setVehicles(data.data as VehRow[]);
      }
    } catch {
      /* silencieux */
    } finally {
      setVehLoading(false);
    }
  }, [userId, authHeaders]);

  useEffect(() => {
    if (fetchVehiclesEnabled && userId) void loadVehicles();
  }, [fetchVehiclesEnabled, userId, loadVehicles]);

  useEffect(() => {
    if (manageId == null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setManageId(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [manageId]);

  useEffect(() => {
    if (manageId == null) return;
    const inv = instances.find((i) => i.id === manageId);
    if ((!inv || isTerminalInterventionStatus(inv.statut || '')) && !completionRewards) {
      setManageId(null);
    }
  }, [manageId, instances, completionRewards]);

  useEffect(() => {
    if (manageId == null) return;
    const id = window.setInterval(() => setUiTick((x) => x + 1), 1000);
    return () => window.clearInterval(id);
  }, [manageId]);

  const manageInvDispatchTotal = useMemo(() => {
    const inv = instances.find((i) => i.id === manageId);
    return inv?.dispatch_total ?? -1;
  }, [instances, manageId]);

  useEffect(() => {
    if (manageId == null || !userId) {
      setSectorVehicles([]);
      setSectorAlreadyEngaged([]);
      setSectorError(null);
      return;
    }
    let cancel = false;
    setSectorLoading(true);
    setSectorError(null);
    void (async () => {
      try {
        const base = getApiBaseUrl();
        const res = await fetch(
          `${base}/api/cta/interventions-instance/${manageId}/sector-vehicles`,
          { headers: authHeadersRef.current() }
        );
        const data = await res.json();
        if (cancel) return;
        if (data.success) {
          setSectorVehicles((data.vehicles || []) as SectorVehicleRow[]);
          setSectorAlreadyEngaged(data.already_engaged || []);
        } else {
          setSectorError(data.error || tRef.current('interventionSidebar.sectorLoadError'));
        }
      } catch {
        if (!cancel) setSectorError(tRef.current('interventionSidebar.sectorLoadError'));
      } finally {
        if (!cancel) setSectorLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [manageId, userId, manageInvDispatchTotal, sectorRefreshNonce]);

  const toggleSectorVehicle = useCallback((instanceId: number, vehId: number) => {
    setSelectedVehIdsByInstance((p) => {
      const cur = p[instanceId] || [];
      const has = cur.includes(vehId);
      const next = has ? cur.filter((x) => x !== vehId) : [...cur, vehId];
      return { ...p, [instanceId]: next };
    });
  }, []);

  const selectAllSectorVehicles = useCallback((instanceId: number, list: SectorVehicleRow[]) => {
    setSelectedVehIdsByInstance((p) => ({
      ...p,
      [instanceId]: list.map((v) => v.id),
    }));
  }, []);

  const clearSectorSelection = useCallback((instanceId: number) => {
    setSelectedVehIdsByInstance((p) => ({ ...p, [instanceId]: [] }));
  }, []);

  const {
    assignVehicle,
    advance,
    unassignReservedVehicle,
    recallInterventionVehicles,
    recallInterventionVehicle,
    reengageInterventionVehicle,
    assignSectorVehicle,
  } = useInterventionVehicleActions({
    authHeaders,
    t,
    setBusyId,
    setActionError,
    setSectorRefreshNonce,
    refreshInstances,
    setInstances,
    loadInstances,
    setSelectedVehIdsByInstance,
    selectedVehIdsByInstance,
    setCompletionRewards,
  });

  const availableVehicles = useMemo(
    () => vehicles.filter((v) => v.id_status === 1 || v.id_status === undefined),
    [vehicles]
  );

  const manageInv = useMemo(() => instances.find((i) => i.id === manageId) ?? null, [instances, manageId]);

  const manageCompletion = useMemo(() => {
    const inv = manageInv;
    if (!inv) {
      return {
        canFinishSuccess: true,
        lines: [] as string[],
        tempsMin: 15,
        req: 1,
        total: 0,
        ph: null as string | null,
        remainingOnSiteSec: null as number | null,
      };
    }
    const req = effectiveMoyensRequired(inv);
    const total = Number(inv.dispatch_total ?? 0);
    const mins = effectiveTempsSurPlaceMinutes(inv);
    const ph = inv.gameplay_phase ?? null;
    const lines: string[] = [];
    let canFinishSuccess = true;
    let remainingOnSiteSec: number | null = null;

    lines.push(
      String(t('interventionSidebar.moyensProgress'))
        .replace(/\{\{cur\}\}/g, String(total))
        .replace(/\{\{req\}\}/g, String(req))
    );

    if (ph === 'ON_SITE') {
      if (total < req) {
        canFinishSuccess = false;
        lines.push(t('interventionSidebar.moyensNeedMore'));
      }
      const allAt = inv.all_units_on_site_at;
      if (total >= req && mins > 0) {
        if (!allAt) {
          canFinishSuccess = false;
          lines.push(t('interventionSidebar.onSiteNotAllArrived'));
        } else {
          const deadline = new Date(allAt).getTime() + mins * 60 * 1000;
          const rem = Math.ceil(Math.max(0, deadline - Date.now()) / 1000);
          if (rem > 0) {
            canFinishSuccess = false;
            remainingOnSiteSec = rem;
            lines.push(
              String(t('interventionSidebar.onSiteWait')).replace(
                /\{\{mmss\}\}/g,
                formatDurationMmSs(rem)
              )
            );
          }
        }
      }
    }

    return { canFinishSuccess, lines, tempsMin: mins, req, total, ph, remainingOnSiteSec };
    // uiTick : rafraîchissement compte à rebours sur place (intervalle 1s)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- uiTick force le recalcul temporel
  }, [manageInv, uiTick, t]);

  const handleDeploy = useCallback(
    (inv: InstanceRow) => {
      const st = inv.statut || '';
      const apiSt = normalizeInterventionInstanceStatut(st);
      const ph = String(inv.gameplay_phase || '').toUpperCase();
      if (ph === 'WAITING' || apiSt === 'EN_ATTENTE') void assignVehicle(inv.id);
      else if (
        ph === 'ASSIGNED' ||
        ph === 'TRAVELING' ||
        ph === 'HOSPITAL_TRANSPORT' ||
        ph === 'HOSPITAL_ARRIVAL' ||
        ph === 'RETURNING'
      )
        void advance(inv.id);
    },
    [assignVehicle, advance]
  );

  const clearCompletionRewards = useCallback(() => {
    setCompletionRewards(null);
    setManageId(null);
  }, []);

  return {
    manageId,
    setManageId,
    vehicles,
    vehLoading,
    availableVehicles,
    selectedVehIdsByInstance,
    toggleSectorVehicle,
    selectAllSectorVehicles,
    clearSectorSelection,
    sectorVehicles,
    sectorAlreadyEngaged,
    sectorLoading,
    sectorError,
    busyId,
    actionError,
    setActionError,
    assignVehicle,
    advance,
    unassignReservedVehicle,
    recallInterventionVehicles,
    recallInterventionVehicle,
    reengageInterventionVehicle,
    assignSectorVehicle,
    handleDeploy,
    manageCompletion,
    completionRewards,
    clearCompletionRewards,
    titleLocale,
  };
}
